import { Link } from '@tanstack/react-router'
import {
  CalendarClock,
  ClipboardList,
  Crown,
  MapPin,
  ShieldQuestion,
  UserRoundCog,
  Users,
} from 'lucide-react'
import type { ReactNode } from 'react'

import type { GroupDetail, GroupMeeting, GroupMember, KycStatus } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime, formatShortDate } from '@/lib/datetime'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

import { describeSchedule, formatTimeOfDay } from './constants'
import { ExitMemberButton } from './membership-actions'
import {
  MEETING_PAGE_SIZE,
  useGroupMeetings,
  useMemberKycStates,
  type MemberKycState,
} from './queries'
import { useGroupPermissions } from './permissions'

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono')}>
        {empty ? <span className="text-muted">—</span> : value}
      </dd>
    </div>
  )
}

function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader className="flex-wrap gap-3">
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {actions}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

/**
 * Who is responsible for this group, where it meets, and when.
 *
 * The officer and the leader sit together because they are the two names a question about this
 * group is put to — one inside the institution and one inside the group — and a group missing
 * either is a group nobody can be sent to.
 */
export function GroupOverviewPanel({ group }: { group: GroupDetail }) {
  const schedule = group.schedules[0]
  const location = group.meetingLocation

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="Group" description="Issued and held by the branch that formed it.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Group code" value={group.groupCode} mono />
          <Field label="Status" value={<StatusBadge status={group.status} />} />
          <Field label="Formed" value={formatShortDate(group.formationDate)} />
          <Field label="Current round" value={group.currentRoundNumber} />
          <Field label="Active members" value={group.activeMemberCount} />
          <Field label="Opened" value={formatDateTime(group.createdAtUtc)} />
        </dl>
      </Section>

      <Section
        title="Responsibility"
        description="One name inside the institution, one inside the group."
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Loan officer"
            value={
              group.loanOfficerName ?? (
                <Badge tone="warning" className="whitespace-normal">
                  <UserRoundCog className="size-3" aria-hidden />
                  No officer assigned
                </Badge>
              )
            }
          />
          <Field
            label="Leader"
            value={
              group.leaderName ?? (
                <Badge tone="warning" className="whitespace-normal">
                  <Crown className="size-3" aria-hidden />
                  No leader appointed
                </Badge>
              )
            }
          />
        </dl>
      </Section>

      <Section
        title="Meeting schedule"
        description={
          group.schedules.length > 1
            ? `${String(group.schedules.length)} schedules recorded; the one in force is shown.`
            : 'The schedule meetings are generated from.'
        }
      >
        {schedule ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Meets" value={describeSchedule(schedule)} />
            <Field label="In force from" value={formatShortDate(schedule.effectiveFrom)} />
            <Field
              label="Until"
              value={schedule.effectiveTo ? formatShortDate(schedule.effectiveTo) : 'Open-ended'}
            />
            <Field
              label="Next meeting"
              value={group.nextMeetingDate ? formatShortDate(group.nextMeetingDate) : null}
            />
          </dl>
        ) : (
          <p className="text-sm text-body">
            Nothing is on the calendar. A schedule needs a meeting location first — a meeting with
            no place is not one anybody can be sent to.
          </p>
        )}
      </Section>

      <Section
        title="Meeting location"
        description="Institutional operating data, held in clear on purpose — this is where the institution collects money, not where anyone lives."
      >
        {location ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field
              label="Place"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="size-3.5 shrink-0 text-brand-text" aria-hidden />
                  {location.name}
                </span>
              }
            />
            <Field label="Address" value={location.address} />
            <Field
              label="Coordinates"
              value={`${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`}
              mono
            />
            <Field
              label="Geofence"
              value={`${String(location.geofenceRadiusMetres)} m`}
              mono
            />
          </dl>
        ) : (
          <p className="text-sm text-body">
            No location is set, so meetings cannot be scheduled and the field application has no
            geofence to validate a collection against.
          </p>
        )}
      </Section>
    </div>
  )
}

/**
 * How a member's KYC file reads *today*.
 *
 * Approved KYC is a condition of joining, not a condition of staying: amending an approved
 * customer's Ghana Card number or date of birth returns their file to `PendingReview` while they
 * carry on being a member. A group whose membership is fine and whose files are not is precisely
 * the case this column exists for, and it is invisible from the group payload alone.
 */
function MemberKyc({ state }: { state: MemberKycState | undefined }) {
  if (!state || state.isPending) {
    return <Skeleton className="h-5 w-20" />
  }

  if (state.isUnavailable || state.kycStatus === undefined) {
    return (
      <Badge
        tone="neutral"
        className="border-dashed"
        title="The customer file could not be read — most often because it is held at a branch this user cannot see."
      >
        <ShieldQuestion className="size-3" aria-hidden />
        Not readable
      </Badge>
    )
  }

  return <StatusBadge status={state.kycStatus satisfies KycStatus} />
}

/**
 * The membership.
 *
 * Active members lead and departed ones follow, in the order the API returns them, because a
 * member who left in March is still part of what this group is and dropping them would make the
 * attendance denominator unexplainable.
 */
export function GroupMembersPanel({
  group,
  onExitMember,
}: {
  group: GroupDetail
  onExitMember: (member: GroupMember) => void
}) {
  const asTable = useIsAtLeast('lg')
  const kycStates = useMemberKycStates(group.members, group.members.length > 0)
  const stateFor = (member: GroupMember) =>
    kycStates.find((state) => state.customerId === member.customerId)

  const dissolved = group.status === 'Dissolved'

  if (group.members.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <Users className="size-6 text-muted" aria-hidden />
        <p className="text-sm font-medium text-heading">Nobody has joined yet</p>
        <p className="max-w-md text-xs text-muted">
          A group in formation has no members until customers with approved KYC files are admitted
          one at a time. Each member is KYC&apos;d individually — the group is never the customer.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-card border border-border-subtle bg-surface">
      {asTable ? (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border-subtle bg-surface-muted">
              <th scope="col" className="px-3 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase">
                Member
              </th>
              <th scope="col" className="w-32 px-3 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase">
                Position
              </th>
              <th scope="col" className="w-32 px-3 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase">
                Membership
              </th>
              <th scope="col" className="w-36 px-3 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase">
                KYC file
              </th>
              <th scope="col" className="w-32 px-3 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase">
                Joined
              </th>
              <th scope="col" className="w-40 px-3 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase">
                Left
              </th>
              <th scope="col" className="w-24 px-3 py-2 text-right text-2xs font-semibold tracking-wide text-muted uppercase">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {group.members.map((member) => (
              <tr key={member.id} className="border-b border-border-subtle last:border-0">
                <td className="px-3 py-2">
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: member.customerId }}
                    className="font-medium text-brand-text hover:text-brand-text-hover"
                  >
                    {member.fullName}
                  </Link>
                  <span className="block font-mono text-2xs text-muted">{member.customerNumber}</span>
                </td>
                <td className="px-3 py-2">
                  {member.position === 'Leader' ? (
                    <Badge tone="brand">
                      <Crown className="size-3" aria-hidden />
                      Leader
                    </Badge>
                  ) : (
                    <span className="text-body">{member.position}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={member.status} />
                </td>
                <td className="px-3 py-2">
                  <MemberKyc state={stateFor(member)} />
                </td>
                <td className="numeric px-3 py-2 whitespace-nowrap text-body tabular-nums">
                  {formatShortDate(member.joinedDate)}
                </td>
                <td className="px-3 py-2 text-body">
                  {member.leftDate ? (
                    <>
                      <span className="numeric block whitespace-nowrap tabular-nums">
                        {formatShortDate(member.leftDate)}
                      </span>
                      {member.exitReason ? (
                        <span className="block text-2xs text-muted">{member.exitReason}</span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <ExitMemberButton member={member} onExit={onExitMember} disabled={dissolved} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {group.members.map((member) => (
            <li key={member.id} className="space-y-3 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: member.customerId }}
                    className="block text-sm font-semibold break-words text-brand-text"
                  >
                    {member.fullName}
                  </Link>
                  <span className="block font-mono text-2xs text-muted">{member.customerNumber}</span>
                </div>
                {member.position === 'Leader' ? (
                  <Badge tone="brand">
                    <Crown className="size-3" aria-hidden />
                    Leader
                  </Badge>
                ) : null}
              </div>

              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Membership" value={<StatusBadge status={member.status} />} />
                <Field label="KYC file" value={<MemberKyc state={stateFor(member)} />} />
                <Field label="Joined" value={formatShortDate(member.joinedDate)} />
                <Field
                  label="Left"
                  value={member.leftDate ? formatShortDate(member.leftDate) : null}
                />
                {member.exitReason ? (
                  <div className="col-span-2">
                    <Field label="Exit reason" value={member.exitReason} />
                  </div>
                ) : null}
              </dl>

              <ExitMemberButton member={member} onExit={onExitMember} disabled={dissolved} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * What is on the calendar, and what happened at the ones that have been.
 *
 * A scheduled meeting carries the control that opens the attendance register; a completed one
 * carries the register itself, including the geofence fix that unlocked it in the field. The two
 * are the same row at different points in its life, which is why they are one list rather than
 * two.
 */
export function GroupMeetingsPanel({ group }: { group: GroupDetail }) {
  const permissions = useGroupPermissions()
  const meetings = useGroupMeetings(group.id, { page: 1, pageSize: MEETING_PAGE_SIZE })

  if (meetings.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if (meetings.isError) {
    return (
      <ErrorSurface
        error={meetings.error}
        onRetry={() => void meetings.refetch()}
        title="The meeting calendar could not be read"
      />
    )
  }

  const rows = meetings.data.items

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <CalendarClock className="size-6 text-muted" aria-hidden />
        <p className="text-sm font-medium text-heading">No meetings on the calendar</p>
        <p className="max-w-md text-xs text-muted">
          Meetings are generated from a schedule, and a schedule needs a meeting location first.
          Nothing is scheduled for this group yet.
        </p>
      </div>
    )
  }

  const unshown = meetings.data.totalCount - rows.length

  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {rows.map((meeting) => (
          <li key={meeting.id}>
            <MeetingRow
              meeting={meeting}
              canRecord={permissions.canRecordAttendance}
              memberCount={group.activeMemberCount}
            />
          </li>
        ))}
      </ul>

      {unshown > 0 ? (
        <p className="text-xs text-muted">
          {unshown} further meeting{unshown === 1 ? ' is' : 's are'} on this group&apos;s calendar,
          beyond the most recent {rows.length} shown here.
        </p>
      ) : null}
    </div>
  )
}

const OPEN_FOR_ATTENDANCE = new Set(['Scheduled', 'InProgress'])

function MeetingRow({
  meeting,
  canRecord,
  memberCount,
}: {
  meeting: GroupMeeting
  canRecord: boolean
  memberCount: number
}) {
  const open = OPEN_FOR_ATTENDANCE.has(meeting.status)

  return (
    <Card>
      <CardHeader className="flex-wrap gap-3">
        <div className="min-w-0 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <span className="numeric tabular-nums">{formatShortDate(meeting.scheduledDate)}</span>
            <span className="numeric text-xs font-normal text-muted tabular-nums">
              {formatTimeOfDay(meeting.scheduledTime)}
            </span>
            <StatusBadge status={meeting.status} />
            {meeting.rolledFromDate ? (
              <Badge
                tone="warning"
                title="Moved off a holiday. The unrolled date still drives the sequence, so the shift does not cascade."
              >
                Rolled from {formatShortDate(meeting.rolledFromDate)}
              </Badge>
            ) : null}
          </CardTitle>
          <p className="text-xs text-muted">
            Round {meeting.roundNumber}
            {meeting.meetingLocationName ? ` · ${meeting.meetingLocationName}` : ''}
          </p>
        </div>

        {open && canRecord ? (
          <Button asChild variant="primary" size="sm">
            <Link to="/groups/$groupId/meetings/$meetingId" params={{ groupId: meeting.groupId, meetingId: meeting.id }}>
              <ClipboardList />
              Record attendance
            </Link>
          </Button>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-3">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <Field label="Expected" value={meeting.expectedCount || memberCount} />
          <Field
            label="Present"
            value={
              meeting.status === 'Completed' || meeting.attendance.length > 0
                ? meeting.presentCount
                : // Not a zero. Nothing has been recorded, and a zero here would read as an
                  // empty room rather than an unopened register.
                  <span className="text-muted">Not recorded</span>
            }
          />
          <Field
            label="Closed"
            value={meeting.closedAtUtc ? formatDateTime(meeting.closedAtUtc) : null}
          />
          <Field
            label="Check-in fix"
            value={
              meeting.checkInDistanceMetres === null ? (
                <span
                  className="text-muted"
                  title="No GPS fix was recorded. A browser has no fix and never fabricates one; the field application supplies a real one."
                >
                  None recorded
                </span>
              ) : (
                <span className="numeric tabular-nums">
                  {meeting.checkInDistanceMetres.toFixed(0)} m from the pin
                </span>
              )
            }
          />
        </dl>

        {meeting.notes ? <p className="text-sm text-body">{meeting.notes}</p> : null}

        {meeting.attendance.length > 0 ? (
          <div className="rounded-card border border-border-subtle">
            <ul className="divide-y divide-border-subtle">
              {meeting.attendance.map((entry) => (
                <li
                  key={entry.customerId}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <span className="min-w-0 text-sm text-body">{entry.fullName}</span>
                  <span className="flex items-center gap-2">
                    {entry.note ? <span className="text-2xs text-muted">{entry.note}</span> : null}
                    <StatusBadge status={entry.status} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : meeting.status === 'Missed' ? (
          <Alert tone="warning">
            <AlertTitle>The group did not meet</AlertTitle>
            <AlertDescription>
              A missed meeting carries no register. It still counts in the attendance denominator,
              which is what makes a run of them show up in the health score.
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}
